import { useEffect, useRef } from 'react';

/**
 * Run `callback` on an interval, but only while the tab is visible, and fire it immediately when
 * the tab/window regains focus. This gives near-real-time updates (others' messages, uploads,
 * status changes) without a manual refresh, at negligible cost — no polling happens in a
 * backgrounded tab, and returning to the tab refreshes at once rather than waiting for the timer.
 *
 * The callback is kept in a ref so a changing closure doesn't reset the interval.
 */
export function usePolling(callback: () => void, intervalMs = 30_000): void {
  const cb = useRef(callback);
  cb.current = callback;

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') cb.current();
      }, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        cb.current(); // refresh immediately on return
        start();
      } else {
        stop();
      }
    };

    // Start if currently visible; always refresh-on-focus.
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, [intervalMs]);
}
